import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';

// Lightweight confirm dialog. Replaces native window.confirm so the styling
// matches the rest of the app and we can show richer multi-line messages.
//
// Props:
//   title          string
//   message        string
//   confirmLabel   string (default: 'Confirm')
//   cancelLabel    string (default: 'Cancel')
//   danger         boolean — red action button + warning icon
//   onConfirm      async function — modal stays mounted (with disabled buttons)
//                  while the promise is pending; closes on resolve, re-enables
//                  on reject so caller can show an error toast.
//   onClose        function — called on cancel, Esc, or backdrop click
export const ConfirmModal = memo(function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  workingLabel = null,   // optional — text shown while onConfirm() resolves
  danger = false,
  onConfirm,
  onClose,
}) {
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !working) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, working]);

  const handleConfirm = async () => {
    setWorking(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      setWorking(false);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget && !working) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        borderRadius: 18,
        boxShadow: '0 24px 60px rgba(0,0,0,.18), 0 6px 20px rgba(0,0,0,.08)',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 22px 14px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 13, flexShrink: 0,
            background: danger ? 'rgba(220,38,38,.1)' : 'rgba(8,145,178,.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>{danger ? '⚠️' : 'ℹ️'}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 720, color: '#0f172a', letterSpacing: '-.2px' }}>{title}</div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6, lineHeight: 1.55 }}>{message}</div>
          </div>
        </div>
        <div style={{
          padding: '12px 22px', borderTop: '1px solid rgba(0,0,0,.06)',
          background: 'rgba(248,250,252,.7)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button className="btn bs" onClick={onClose} disabled={working}>{cancelLabel}</button>
          <button
            className={danger ? 'btn bd' : 'btn bp'}
            onClick={handleConfirm}
            disabled={working}
            style={{ minWidth: 110, justifyContent: 'center', gap: 8 }}
          >
            {working
              ? (workingLabel
                  ? <><span className="ec-spin" aria-hidden /> {workingLabel}</>
                  : '…')
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
});
